// curium:allow-copy-file — verbatim terms of use; wording changes go through legal review
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { RequireAuth } from '@client/components/auth/RequireAuth';
import { trpcOptions } from '@client/trpc/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Typography } from 'antd';
import { type ReactNode, useState } from 'react';
import { z } from 'zod';

const { Title, Text } = Typography;

export const Route = createFileRoute('/terms-acceptance')({
    validateSearch: zodValidator(z.object({ redirectTo: z.string().optional() })),
    component: TermsAcceptance,
});

function TermsAcceptance() {
    const queryClient = useQueryClient();
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const { redirectTo = '/' } = Route.useSearch();

    const { mutate: acceptTerms, isPending } = useMutation(
        trpcOptions.user.acceptTerms.mutationOptions({
            onSuccess: () => {
                // Update cache so future checks reflect acceptance
                queryClient.setQueryData(trpcOptions.user.getTermsAccepted.queryKey(), true);
                // Redirect to original destination
                const decodedRedirect = decodeURIComponent(redirectTo);
                window.location.href = decodedRedirect;
            },
            onError: (err) => {
                errorMessage.show(err);
            },
        }),
    );

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const element = e.currentTarget;
        const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
        if (isAtBottom && !hasScrolledToBottom) {
            setHasScrolledToBottom(true);
        }
    };

    const handleAccept = () => {
        acceptTerms();
    };

    const handleCancel = () => {
        window.location.href = 'https://www.getcurium.com';
    };

    return (
        <PageController route={Route}>
            <RequireAuth>
                <div className="flex min-h-screen w-full items-center justify-center bg-muted px-6 py-8 md:px-8">
                    <div className="w-full max-w-4xl">
                        <div className="w-full rounded-lg border border-separator bg-card p-6 shadow-md md:p-8">
                            <Title level={2} className="mb-6 text-center font-semibold text-2xl md:mb-8">
                                CURIUM EXTERNAL USER AGREEMENT
                            </Title>

                            <div
                                data-testid="terms-container"
                                className="mb-6 max-h-[60vh] overflow-y-auto rounded-lg border border-separator bg-muted p-6 md:mb-8 md:p-8"
                                onScroll={handleScroll}
                            >
                                <div className="prose prose-base max-w-none text-muted-foreground">
                                    <TermsSectionHeading>1. BACKGROUND</TermsSectionHeading>
                                    <p className="mb-4">
                                        Curium is a modular Software as a Service (SaaS) technology designed for the
                                        insurance industry to deliver simple and intuitive out-of-the-box insurance
                                        solutions. These Terms & Conditions ("Agreement" or "Terms") govern Your use of
                                        Curium services as an External User. By accepting this Agreement, either by
                                        checking the box indicating your acceptance or by executing an Account Request
                                        that references this Agreement, you agree to the terms and conditions of this
                                        Agreement and these Terms will then apply to You from the time that you first
                                        access the Services.
                                    </p>
                                    <p className="mb-4">
                                        If you are entering into this Agreement on behalf of a company or other legal
                                        entity, You represent and warrant that you have the authority to bind such an
                                        entity and its affiliates to these Terms, in which case the terms "You" or
                                        "Your" shall refer to such entity and its affiliates. If You do not have such
                                        authority, or if You do not agree with these Terms, You must not accept this
                                        Agreement and may not use the Services.
                                    </p>
                                    <p className="mb-4">
                                        Curium reserves the right to amend these Terms at any time, effective on the
                                        notification to You of such modified Terms. It is likely that the Terms will
                                        change over time. It is Your obligation to ensure that You have read and
                                        understood the most recent Terms available.
                                    </p>

                                    <TermsSectionHeading>2. DEFINITIONS</TermsSectionHeading>
                                    <p className="mb-4">
                                        <strong>"Agreement"</strong> or <strong>"Terms"</strong> means these Curium
                                        Terms and Conditions and includes any notices, policies, guidelines or
                                        conditions sent to You by Curium or posted on the Website.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Affiliates"</strong> means any entity that directly or indirectly
                                        controls, is controlled by, or is under common control by You (for example, one
                                        of Your subsidiaries).
                                    </p>
                                    <p className="mb-4">
                                        <strong>"You"</strong> and <strong>"Your"</strong> means the External User.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Confidential Information"</strong> means, in relation to a party, any
                                        information (whether in oral, written or electronic form) that is by its nature
                                        confidential, is designated by that party to be confidential or that a party
                                        knows or ought to know is confidential, whether exchanged between the parties
                                        before, on or after the date of this Agreement, including but not limited to
                                        any:
                                    </p>
                                    <ul>
                                        <li>Intellectual Property;</li>
                                        <li>
                                            Information regarding current or future business or affairs (including
                                            financial position, sales, internal management, structure, policies,
                                            methodology, strategies, business plans and forecasts, advertising,
                                            marketing or promotional activities);
                                        </li>
                                        <li>
                                            Information regarding customers, (including any transactional data, queries,
                                            complaints or disputes) or any Personal Information; and
                                        </li>
                                        <li>Trade secret, know-how or business process,</li>
                                    </ul>
                                    <p className="mb-4">but does not include information that:</p>
                                    <ul>
                                        <li>
                                            the party rightfully possessed before it received the information from the
                                            other party as evidenced by written documentation;
                                        </li>
                                        <li>subsequently becomes publicly available through no fault of that party;</li>
                                        <li>
                                            is subsequently furnished to that party by a third party (no Affiliate of
                                            either party shall be considered to be a third party for the purpose of this
                                            definition only) free of any obligation of confidentiality to that third
                                            party; or
                                        </li>
                                        <li>
                                            is independently developed by an employee, agent or sub-contractor of such
                                            party or of any of such party's Affiliates.
                                        </li>
                                    </ul>
                                    <p className="mb-4">
                                        <strong>"Curium"</strong> means Curium Co Pty Ltd, ACN 651 164 171 and also
                                        means the modular software as a service technology to which You will be granted
                                        access subject to the terms and conditions of this Agreement, as updated and
                                        modified from time to time.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Data"</strong> means any photos, images, videos, graphics, written
                                        content, audio files, code, information or data inputted or uploaded by You
                                        (including by an External User) into the Curium application processed or made
                                        available to You or others as a direct result of Your use of the Services and
                                        the Curium application.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Intellectual Property"</strong> means copyrights (including moral
                                        rights), patents, trademarks, service marks and designs, domain names, business
                                        or company names, know-how, trade secrets and competitively sensitive material,
                                        operational service documents user manuals, online guides, screen displays and
                                        formats, computer equipment and software, circuit layouts, methodologies,
                                        systems, products, system architecture, functional and technical specifications,
                                        database rights, data and data formats, whether owned or licensed or otherwise
                                        provided by a party, whether registered or unregistered, whether or not
                                        electronically stored or otherwise embodied, recorded or documented, whether
                                        existing in Australia or otherwise and includes present and future rights,
                                        applications for registration, the right to apply for registration for, and
                                        documentation related to any of the same.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"External User"</strong> means a unique individual authorised to use the
                                        Services by a Curium Client, for the benefit of the Client, and in the capacity
                                        determined by the Client.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Loss"</strong> means all liabilities, expenses, losses, damages and
                                        costs (including legal costs) and expenses, however arising.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Services"</strong> means the online Curium software, Curium platform
                                        and modules, and any other Curium platforms, applications or products that You
                                        have subscribed, agreed to receive, or that Curium otherwise makes available to
                                        You via the Website, as may be varied from time to time.
                                    </p>
                                    <p className="mb-4">
                                        <strong>"Website"</strong> means the Internet site at the domain
                                        https://www.curium.app/ and any other sites and mobile apps operated by Curium.
                                    </p>
                                    <p className="mb-4">
                                        Any reference to <strong>"includes"</strong> or <strong>"including"</strong> is
                                        on a without limitation basis.
                                    </p>

                                    <TermsSectionHeading>3. YOUR OBLIGATIONS</TermsSectionHeading>
                                    <TermsClauseHeading>3.1. General obligations</TermsClauseHeading>
                                    <p className="mb-4">
                                        You are solely responsible for your use of the Services and Website and for Your
                                        Data and for ensuring at all times that Your use of the Services and Website and
                                        Your Data is compliant with applicable laws and regulations. You must only use
                                        the Services and Website for Your own lawful internal business purposes, in
                                        accordance with these Terms and any notices, policies, guidelines or conditions
                                        sent to You by Curium or posted on the Website.
                                    </p>

                                    <TermsClauseHeading>3.2. Access conditions</TermsClauseHeading>
                                    <p className="mb-4">
                                        <strong>a.</strong> You must ensure that Your usernames and passwords required
                                        to access the Services and Website are kept secure and confidential. You must
                                        immediately notify Curium of any unauthorised use of Your passwords or any other
                                        breach of security and Curium will reset Your password(s) and You must take all
                                        other actions that Curium reasonably deems necessary to maintain or enhance the
                                        security of Curium's computing systems and networks (including the Website) and
                                        Your access to the Services.
                                    </p>
                                    <p className="mb-4">
                                        <strong>b.</strong> When accessing and using the Services You must not:
                                    </p>
                                    <ul>
                                        <li>
                                            attempt to undermine the security or integrity of Curium's computing systems
                                            or networks (including the Website);
                                        </li>
                                        <li>
                                            use, or misuse, the Services in any way which may impair the functionality
                                            of the Services, Website or other systems used to deliver the Services or
                                            impair the ability of any other user to use the Services or Website,
                                            including by misusing the Services in a manner that materially exceeds
                                            reasonable usage or use patterns over any month or by using the Services in
                                            a malicious, fraudulent or unlawful manner;
                                        </li>
                                        <li>
                                            attempt to gain unauthorised access to any materials other than those to
                                            which You have been given express permission to access or to the computer
                                            system on which the Services are hosted;
                                        </li>
                                        <li>
                                            transmit, input or upload to the Website, any files that may damage any
                                            other person's computing devices or software, content that may be offensive,
                                            or material or Data in violation of any law (including Data or other
                                            material protected by copyright or trade secrets which You do not have the
                                            right to use);
                                        </li>
                                        <li>
                                            attempt to modify, copy, adapt, reproduce, disassemble, decompile or reverse
                                            engineer any computer programs used to deliver the Services or to operate
                                            the Website except as is strictly necessary to use either of them for normal
                                            operation; or
                                        </li>
                                        <li>
                                            provide access to the Services to any competitor of Curium, or any person or
                                            entity which may compete with Curium's Services.
                                        </li>
                                    </ul>

                                    <TermsClauseHeading>3.3. Usage reviews</TermsClauseHeading>
                                    <p className="mb-4">
                                        You agree that Curium may review Your use of the Services at any time, and You
                                        will provide any reasonable assistance to verify Your compliance with this
                                        Agreement as we may request. Curium may suspend Your rights to access and use of
                                        the Services (including Your rights to access any of Your Data) immediately on
                                        written notice if we determine that Your use of the Services (including any of
                                        Your Data) breaches this Agreement (without limiting any other rights or
                                        remedies Curium may have).
                                    </p>

                                    <TermsClauseHeading>3.4. Communication Conditions</TermsClauseHeading>
                                    <p className="mb-4">
                                        As a condition of these Terms, if You use any communication tools available
                                        through the Website or Curium, You agree only to use such communication tools
                                        for lawful and legitimate purposes. You must not use any such communication tool
                                        for posting or disseminating any material unrelated to the use of the Services,
                                        including: offers of goods or services for sale, unsolicited commercial e-mail,
                                        files that may damage any other person's computing devices or software, content
                                        that may be offensive to any other users of the Services or the Website, or
                                        material in violation of any law (including material that is protected by
                                        copyright or trade secrets which You do not have the right to use).
                                    </p>
                                    <p className="mb-4">
                                        When You generate any communication on the Website or via Curium, You represent
                                        that You are permitted to generate such communication. Curium is under no
                                        obligation to ensure that the communications on the Website or Curium are
                                        legitimate or that they are related only to the use of the Services. You must
                                        exercise caution when using the communication tools available on the Website or
                                        Curium. Curium reserves the right to remove any communication at any time in its
                                        sole discretion.
                                    </p>

                                    <TermsSectionHeading>4. CONFIDENTIALITY AND PRIVACY</TermsSectionHeading>
                                    <TermsClauseHeading>4.1. Confidentiality</TermsClauseHeading>
                                    <p className="mb-4">
                                        Unless the relevant party has the prior written consent of the other or unless
                                        required to do so by law:
                                    </p>
                                    <ul>
                                        <li>
                                            Each party will preserve the confidentiality of all Confidential Information
                                            of the other obtained in connection with these Terms.
                                        </li>
                                        <li>
                                            Neither party will, without the prior written consent of the other, disclose
                                            or make any Confidential Information available to any person, or use the
                                            same for its own benefit, other than as expressly contemplated by these
                                            Terms or, where Curium is the recipient, in order to provide the Services to
                                            You.
                                        </li>
                                        <li>
                                            Each party's obligations under this clause will survive termination of these
                                            Terms.
                                        </li>
                                    </ul>

                                    <TermsClauseHeading>4.2. Privacy</TermsClauseHeading>
                                    <p className="mb-4">
                                        You are responsible for ensuring you do not breach any Australian Privacy Laws
                                        in your use of the Services, including in the management of any third party's
                                        personal information. Curium will not be responsible for the management of any
                                        personal data uploaded to the Curium systems by You, your Users, or your
                                        External Users, and You agree to hold Curium harmless, and indemnify Curium
                                        against all claims, actions, suits, or other in contract, tort (including
                                        negligence), or otherwise, for any Losses (whether direct or indirect) arising
                                        out of, or in connection with any use or disclosure of personal information, or
                                        breach of privacy, caused by, or related to, Your use of the Services, or the
                                        Use of Your Users or External Users.
                                    </p>
                                    <p className="mb-4">
                                        Curium will provide the Services in accordance with all relevant Australian
                                        Privacy Laws and in accordance with{' '}
                                        <a
                                            href="https://getcurium.com/app/uploads/2025/10/2025-10-17-Curium-Privacy-Policy.pdf"
                                            target="_blank"
                                            rel="noreferrer noopener"
                                        >
                                            Curium's Privacy Policy
                                        </a>{' '}
                                        which is available on its Website.
                                    </p>

                                    <TermsSectionHeading>5. INTELLECTUAL PROPERTY</TermsSectionHeading>
                                    <TermsClauseHeading>5.1. General</TermsClauseHeading>
                                    <p className="mb-4">
                                        Title to, and all Intellectual Property Rights in the Services, the Website, the
                                        Curium and any documentation relating to the Services remain the property of
                                        Curium only. Curium will remain the sole owner of the Services and any
                                        Intellectual Property that may be available to protect the Services, regardless
                                        of jurisdiction and without regard to whether registration of the Intellectual
                                        Property has been or will be sought by Curium and neither You nor any of Your
                                        Affiliates shall acquire any licence of, right or claim to, or interest in the
                                        Services and any Intellectual Property that is or may be associated therewith
                                        either during or after the Term of this Agreement.
                                    </p>
                                    <p className="mb-4">
                                        You will take such action as reasonably required to protect Curium's
                                        Intellectual Property in the Services. You agree to comply with any Curium
                                        operating requirements or terms of use relating to the Services as may be
                                        notified or published in print or electronically (including online) from time to
                                        time by Curium.
                                    </p>

                                    <TermsClauseHeading>5.2. Ownership of Data</TermsClauseHeading>
                                    <p className="mb-4">
                                        Title to, and all Intellectual Property rights in, the Data remain Your
                                        property. You grant Curium a licence to use, copy, transmit, store, and back-up
                                        Your Data for the purposes of enabling You to access and use the Services and
                                        for any other purpose related to provision of Services to You. For the avoidance
                                        of doubt, this licence is without limitation to Curium's right to create
                                        anonymised data compilations or similar works based on Your Data.
                                    </p>

                                    <TermsClauseHeading>5.3. Accuracy of Data</TermsClauseHeading>
                                    <p className="mb-4">
                                        You agree and acknowledge that You are responsible for ensuring the accuracy of
                                        Data inputted into the Website and/or Curium by You or by any person on Your
                                        behalf. Curium is under no obligation to ensure that Your Data in the Website
                                        and/or Curium is an accurate representation of Your actual business data.
                                    </p>

                                    <TermsSectionHeading>6. WARRANTIES AND ACKNOWLEDGEMENTS</TermsSectionHeading>
                                    <TermsClauseHeading>6.1. Data</TermsClauseHeading>
                                    <p className="mb-4">You represent and warrant that:</p>
                                    <ul>
                                        <li>
                                            You have obtained all necessary consents and are otherwise authorised to
                                            input or upload the Data that You input or upload to the Website, including
                                            any Data inputted or uploaded to the Website by any External User or by a
                                            User on Your behalf; and
                                        </li>
                                        <li>
                                            You are similarly authorised to access the processed Data that is made
                                            available to You through Your use of the Website and the Services, in each
                                            case, whether the inputted or uploaded Data is Your own or that of anyone
                                            else.
                                        </li>
                                    </ul>

                                    <TermsClauseHeading>6.2. Acknowledgements and disclaimers</TermsClauseHeading>
                                    <p className="mb-4">
                                        You acknowledge and agree that Curium gives no warranty about the Services and
                                        Website and, without limiting the foregoing:
                                    </p>
                                    <ul>
                                        <li>
                                            The provision of, access to, and use of, the Services and the Website is on
                                            an "as is" basis and at Your own risk.
                                        </li>
                                        <li>
                                            Curium does not warrant that the use of the Services or the Website will be
                                            uninterrupted or error free. Among other things, the operation and
                                            availability of the systems used for accessing the Service and the Website,
                                            including public telephone services, computer networks and the Internet, can
                                            be unpredictable and may from time to time interfere with or prevent access
                                            to the Services and the Website and/or Curium. Curium is not in any way
                                            responsible for any such interference or prevention of Your access or use of
                                            the Services which is outside Curium's reasonable control.
                                        </li>
                                        <li>
                                            Curium does not warrant that the Services or the Website and/or Curium will
                                            meet Your requirements or that the Services or the Website will be suitable
                                            for any particular purpose. It is Your sole responsibility to determine that
                                            the Services and the Website and/or Curium meet the needs of Your business
                                            and are suitable for the purposes for which they are used.
                                        </li>
                                        <li>
                                            You remain solely responsible for complying with all applicable accounting,
                                            tax and other laws. It is Your responsibility to check that storage of and
                                            access to Your Data via the Website will comply with laws applicable to You
                                            (including any laws requiring You to retain records).
                                        </li>
                                    </ul>

                                    <TermsClauseHeading>6.3. No implied warranties</TermsClauseHeading>
                                    <p className="mb-4">
                                        To avoid doubt, all implied conditions or warranties are excluded in so far as
                                        is permitted by law, including warranties of merchantability and fitness for
                                        purpose, title and non-infringement.
                                    </p>

                                    <TermsSectionHeading>7. INDEMNITY AND LIMITATION OF LIABILITY</TermsSectionHeading>
                                    <TermsClauseHeading>7.1. Indemnity</TermsClauseHeading>
                                    <p className="mb-4">
                                        You indemnify Curium against all Losses that Curium incurs arising out of or in
                                        connection with:
                                    </p>
                                    <ul>
                                        <li>Your breach of any of these Terms; and/or</li>
                                        <li>
                                            any third party claims against Curium relating to Your Data or otherwise to
                                            Your use of the Services or Website; and/or
                                        </li>
                                        <li>
                                            any loss suffered by Curium to the extent it arises out of or is caused by
                                            the negligent act or omission of You, Your Affiliate, servant, agent,
                                            contractor or customer.
                                        </li>
                                    </ul>

                                    <TermsClauseHeading>7.2. Curium is not responsible</TermsClauseHeading>
                                    <p className="mb-4">
                                        To the maximum extent permitted by law, Curium will not be liable to You (or any
                                        other person) in contract, tort (including negligence), or otherwise, for any
                                        Losses (whether direct or indirect) arising out of, or in connection with, Your
                                        use of, or reliance on, the Services or Website and/or Curium or otherwise
                                        arising out of or in connection with these Terms.
                                    </p>

                                    <TermsSectionHeading>8. GENERAL</TermsSectionHeading>
                                    <TermsClauseHeading>8.1. Entire agreement</TermsClauseHeading>
                                    <p className="mb-4">
                                        These Terms, and the terms of any other notices or instructions given to You
                                        under these Terms, supersede and extinguish all prior agreements,
                                        representations (whether oral or written), and understandings and constitute the
                                        entire agreement between You and Curium relating to the Services and Your access
                                        and use of the Website and/or Curium and the other matters dealt with in these
                                        Terms.
                                    </p>

                                    <TermsClauseHeading>8.2. Waiver</TermsClauseHeading>
                                    <p className="mb-4">
                                        A waiver of any breach of any provision in these Terms shall not be effective
                                        unless that waiver is in writing and is signed by the party against whom that
                                        waiver is claimed. If either party waives any breach of these Terms, this will
                                        not constitute a waiver of any other breach. No waiver will be effective unless
                                        made in writing.
                                    </p>

                                    <TermsClauseHeading>8.3. No Assignment</TermsClauseHeading>
                                    <p className="mb-4">
                                        You may not assign or transfer this Agreement or any of Your rights under this
                                        Agreement to another person without our prior written consent.
                                    </p>

                                    <TermsClauseHeading>8.4. Governing law and jurisdiction</TermsClauseHeading>
                                    <p className="mb-4">
                                        This Agreement is governed by, and construed in accordance with, the internal
                                        laws of the state of New South Wales without giving effect to any choice or
                                        conflict of law provision.
                                    </p>

                                    <TermsClauseHeading>8.5. Severability</TermsClauseHeading>
                                    <p className="mb-4">
                                        If any part or provision of these Terms is invalid, unenforceable or in conflict
                                        with the law, that part or provision is replaced with a provision which, as far
                                        as possible, accomplishes the original purpose of that part or provision. The
                                        remainder of this Agreement will be binding on the parties.
                                    </p>

                                    <TermsClauseHeading>8.6. Notices</TermsClauseHeading>
                                    <p className="mb-4">
                                        Any notice given under these Terms must be in writing by email and will be
                                        deemed to have been given on transmission unless an electronic error message is
                                        received by the sender. Notices to Curium must be sent to tetiana@curium.app.
                                        Notices to You will be sent to the email address(es) on record in our account
                                        information for You. You must keep all Your account information current.
                                    </p>
                                </div>
                            </div>

                            {!hasScrolledToBottom && (
                                <div className="mb-6 rounded-lg bg-warning-subtle p-4 text-center">
                                    <Text className="text-warning-foreground">
                                        Please scroll to the bottom to read all terms before accepting
                                    </Text>
                                </div>
                            )}

                            <div className="flex justify-center gap-4">
                                <Button onClick={handleCancel} size="large" type="secondary" className="min-w-[120px]">
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleAccept}
                                    disabled={!hasScrolledToBottom}
                                    loading={isPending}
                                    size="large"
                                    className="min-w-[120px]"
                                >
                                    Accept Terms
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </RequireAuth>
        </PageController>
    );
}

// The agreement page has no app shell or PageLayoutHeader, so the agreement keeps its own outline.
function TermsSectionHeading({ children }: { children: ReactNode }) {
    // curium:allow-heading — terms acceptance page, outside the app shell
    return <h2>{children}</h2>;
}

function TermsClauseHeading({ children }: { children: ReactNode }) {
    // curium:allow-heading — terms acceptance page, outside the app shell
    return <h3>{children}</h3>;
}
